// ========================================
// TEAM CREATE MODAL - CREATE NEW TEAM
// ========================================

import React, { useState, useMemo, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import Modal from './shared/Modal';
import Button from './shared/Button';
import { createTeam, getLiteLLMUsers } from '../services/api';
import { DURATION_MAP } from '../constants/dashboard';

/**
 * TeamCreateModal Component
 *
 * Modal for creating a new team with budget, model access, and initial members.
 * Features:
 * - Set team name (team_alias)
 * - Set initial budget and duration
 * - Select models for team access
 * - Optionally add users during creation
 *
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onUpdate - Callback after team creation
 * @param {Array} allModels - List of all available models
 * @param {Array} allUsers - List of OpenWebUI users (for OpenWebUI group assignment)
 */
const TeamCreateModal = ({ onClose, onUpdate, allModels = [], allUsers = [] }) => {
    const { accounts } = useMsal();
    const { t } = useTranslation();
    const currentUserEmail = accounts[0]?.username || accounts[0]?.email || 'unknown@aware.co.th';

    const [teamAlias, setTeamAlias] = useState('');
    const [budget, setBudget] = useState('');
    const [duration, setDuration] = useState('monthly');
    const [selectedModels, setSelectedModels] = useState([]);
    const [selectedUserEmails, setSelectedUserEmails] = useState([]);
    const [selectedUserRoles, setSelectedUserRoles] = useState({}); // { email: 'admin' | 'user' }
    const [userSearch, setUserSearch] = useState('');
    const [defaultLiteLLMUserId, setDefaultLiteLLMUserId] = useState('');
    const [defaultUserRole, setDefaultUserRole] = useState('admin'); // Default role for the default user
    const [createApiKey, setCreateApiKey] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [createdApiKey, setCreatedApiKey] = useState(null);

    // LiteLLM users state
    const [litellmUsers, setLitellmUsers] = useState([]);
    const [litellmUsersLoading, setLitellmUsersLoading] = useState(true);

    /**
     * Fetch LiteLLM internal users on mount
     * These users have the correct user_id (UUID) needed for LiteLLM team membership
     */
    useEffect(() => {
        const fetchLiteLLMUsers = async () => {
            setLitellmUsersLoading(true);
            const result = await getLiteLLMUsers();
            if (result.success) {
                setLitellmUsers(result.data || []);
            } else {
                console.error('[TeamCreateModal] Failed to fetch LiteLLM users:', result.error);
                setError('Failed to load users. Please try again.');
            }
            setLitellmUsersLoading(false);
        };

        fetchLiteLLMUsers();
    }, []);

    /**
     * Validate form inputs
     */
    const validateForm = () => {
        if (!teamAlias.trim()) {
            setError(t('teamCreate.errors.nameRequired'));
            return false;
        }

        if (!budget || parseFloat(budget) <= 0) {
            setError(t('teamCreate.errors.budgetRequired'));
            return false;
        }

        if (selectedModels.length === 0) {
            setError(t('teamCreate.errors.modelsRequired'));
            return false;
        }

        if (!defaultLiteLLMUserId) {
            setError('Please select a default LiteLLM user for the team');
            return false;
        }

        return true;
    };

    /**
     * Handle team creation
     */
    const handleCreate = async () => {
        if (!validateForm()) {
            return;
        }

        setCreating(true);
        setError(null);

        // Map selected emails to both OpenWebUI user IDs and LiteLLM user IDs
        const initialUserIds = allUsers
            .filter(u => selectedUserEmails.includes(u.email))
            .map(u => u.id);

        const litellmUserIds = litellmUsers
            .filter(u => selectedUserEmails.includes(u.user_email))
            .map(u => u.user_id);

        // Map email-based roles to user_id-based roles for LiteLLM
        const userRoles = {};
        litellmUsers.forEach(liteUser => {
            if (selectedUserEmails.includes(liteUser.user_email)) {
                userRoles[liteUser.user_id] = selectedUserRoles[liteUser.user_email] || 'user';
            }
        });

        const teamData = {
            team_alias: teamAlias.trim(),
            max_budget: parseFloat(budget),
            budget_duration: DURATION_MAP[duration] || '30d',
            models: selectedModels,
            initialUserIds: initialUserIds,  // OpenWebUI user IDs for group membership
            litellmUserIds: litellmUserIds,  // LiteLLM user IDs for team membership
            defaultLiteLLMUserId: defaultLiteLLMUserId,  // Default LiteLLM internal user
            defaultUserRole: defaultUserRole,  // Role for the default user
            userRoles: userRoles,  // Roles for additional LiteLLM users
            createApiKey: createApiKey  // Whether to create an API key for this team
        };

        const result = await createTeam(teamData, currentUserEmail);

        if (result.success) {
            // If an API key was created, show it to the user
            if (result.data?.apiKey) {
                setCreatedApiKey(result.data.apiKey);
                // Don't close modal yet - let user copy the API key
            } else {
                // No API key created, close immediately
                if (onUpdate) {
                    await onUpdate();
                }
                onClose();
            }
        } else {
            console.error('[TEAM CREATE] Error creating team:', result.error);
            setError(result.error || t('teamCreate.errors.createFailed'));
        }

        setCreating(false);
    };

    /**
     * Toggle model selection
     */
    const toggleModel = (modelId) => {
        setSelectedModels(prev => {
            if (prev.includes(modelId)) {
                return prev.filter(id => id !== modelId);
            } else {
                return [...prev, modelId];
            }
        });
    };

    /**
     * Toggle user selection (by email)
     * We use email as the key since it's common between OpenWebUI and LiteLLM users
     */
    const toggleUser = (userEmail) => {
        setSelectedUserEmails(prev => {
            if (prev.includes(userEmail)) {
                return prev.filter(email => email !== userEmail);
            } else {
                return [...prev, userEmail];
            }
        });
    };

    /**
     * Extract model name from OpenWebUI models (removes team prefixes)
     */
    const getModelName = (model) => {
        let name = model.id || model.model_name || (typeof model === 'string' ? model : null);
        if (!name) return null;

        // Remove OpenWebUI team prefix (e.g., "PLUS.gpt-5" -> "gpt-5")
        const parts = name.split('.');
        if (parts.length > 1 && ['PLUS', 'BKK', 'CNX'].includes(parts[0])) {
            name = parts.slice(1).join('.');
        }

        return name;
    };

    /**
     * Get unique model names from all models (removing duplicates and team prefixes)
     */
    const uniqueModels = useMemo(() => {
        const modelNames = allModels
            .map(m => getModelName(m))
            .filter(Boolean)
            .filter(name => !name.toLowerCase().includes('auto-router') && !name.toLowerCase().includes('router'));

        // Remove duplicates
        return [...new Set(modelNames)].sort();
    }, [allModels]);

    /**
     * Select all models
     */
    const selectAllModels = () => {
        setSelectedModels([...uniqueModels]);
    };

    /**
     * Clear all model selections
     */
    const clearAllModels = () => {
        setSelectedModels([]);
    };

    /**
     * Merge LiteLLM users with OpenWebUI users for display
     * We show LiteLLM users (which have correct UUIDs) but display OpenWebUI names when available
     */
    const mergedUsers = useMemo(() => {
        const userMap = new Map();

        // Add all LiteLLM users first (these have the correct UUIDs)
        litellmUsers.forEach(liteUser => {
            if (liteUser.user_email) {
                userMap.set(liteUser.user_email.toLowerCase(), {
                    email: liteUser.user_email,
                    name: liteUser.user_email.split('@')[0], // Default name from email
                    litellmUserId: liteUser.user_id,
                    source: 'litellm'
                });
            }
        });

        // Enhance with OpenWebUI user names if available
        allUsers.forEach(owUser => {
            if (owUser.email) {
                const key = owUser.email.toLowerCase();
                if (userMap.has(key)) {
                    const existing = userMap.get(key);
                    existing.name = owUser.name || existing.name;
                    existing.openwebuiUserId = owUser.id;
                } else {
                    // User exists in OpenWebUI but not LiteLLM yet - still show them
                    userMap.set(key, {
                        email: owUser.email,
                        name: owUser.name || owUser.email.split('@')[0],
                        openwebuiUserId: owUser.id,
                        source: 'openwebui'
                    });
                }
            }
        });

        return Array.from(userMap.values()).sort((a, b) =>
            (a.name || '').localeCompare(b.name || '')
        );
    }, [litellmUsers, allUsers]);

    /**
     * Filter users by search term
     */
    const filteredUsers = mergedUsers.filter(user => {
        if (!userSearch.trim()) return true;
        const search = userSearch.toLowerCase();
        return (
            (user.name && user.name.toLowerCase().includes(search)) ||
            (user.email && user.email.toLowerCase().includes(search))
        );
    });

    /**
     * Handle closing modal after API key is copied
     */
    const handleCloseWithApiKey = async () => {
        if (onUpdate) {
            await onUpdate();
        }
        onClose();
    };

    /**
     * Copy API key to clipboard
     */
    const copyApiKey = () => {
        if (createdApiKey) {
            navigator.clipboard.writeText(createdApiKey);
        }
    };

    // If API key was created, show success screen with key
    if (createdApiKey) {
        return (
            <Modal
                isOpen={true}
                onClose={handleCloseWithApiKey}
                title="Team Created Successfully!"
                maxWidth="600px"
            >
                <div style={{ padding: '1.5rem' }}>
                    <div style={{
                        padding: '1rem',
                        backgroundColor: 'var(--success)',
                        color: 'white',
                        borderRadius: '6px',
                        marginBottom: '1.5rem',
                        textAlign: 'center'
                    }}>
                        ✓ Team "{teamAlias}" has been created
                    </div>

                    <div style={{
                        padding: '1.5rem',
                        backgroundColor: 'var(--warning-bg)',
                        border: '2px solid var(--warning)',
                        borderRadius: '6px',
                        marginBottom: '1.5rem'
                    }}>
                        <div style={{
                            fontWeight: '600',
                            color: 'var(--warning)',
                            marginBottom: '0.5rem',
                            fontSize: '1.1rem'
                        }}>
                            API Key Created
                        </div>
                        <div style={{ color: 'var(--warning)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                            Save this API key now. You won't be able to see it again!
                        </div>
                        <div style={{
                            padding: '0.75rem',
                            backgroundColor: 'var(--surface)',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            color: 'var(--fg-primary)',
                            wordBreak: 'break-all',
                            marginBottom: '0.75rem'
                        }}>
                            {createdApiKey}
                        </div>
                        <Button
                            onClick={copyApiKey}
                            variant="primary"
                            style={{ width: '100%' }}
                        >
                            📋 Copy API Key
                        </Button>
                    </div>

                    <Button
                        onClick={handleCloseWithApiKey}
                        variant="primary"
                        style={{ width: '100%' }}
                    >
                        Done
                    </Button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={t('teamCreate.title')}
            maxWidth="600px"
        >
            <div style={{ padding: '1.5rem' }}>
                {/* Team Name */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '600',
                        color: 'var(--fg-primary)'
                    }}>
                        {t('teamCreate.teamName')} *
                    </label>
                    <input
                        type="text"
                        value={teamAlias}
                        onChange={(e) => setTeamAlias(e.target.value)}
                        placeholder={t('teamCreate.teamNamePlaceholder')}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            backgroundColor: 'var(--surface)',
                            color: 'var(--fg-primary)',
                            fontSize: '1rem'
                        }}
                        disabled={creating}
                    />
                </div>

                {/* Budget Section */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem',
                    marginBottom: '1.5rem'
                }}>
                    {/* Budget Amount */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            color: 'var(--fg-primary)'
                        }}>
                            {t('teamCreate.budget')} ($) *
                        </label>
                        <input
                            type="number"
                            value={budget}
                            onChange={(e) => setBudget(e.target.value)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                backgroundColor: 'var(--surface)',
                                color: 'var(--fg-primary)',
                                fontSize: '1rem'
                            }}
                            disabled={creating}
                        />
                    </div>

                    {/* Duration */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            color: 'var(--fg-primary)'
                        }}>
                            {t('teamCreate.duration')} *
                        </label>
                        <select
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                backgroundColor: 'var(--surface)',
                                color: 'var(--fg-primary)',
                                fontSize: '1rem'
                            }}
                            disabled={creating}
                        >
                            <option value="daily">{t('teamManagement.daily')}</option>
                            <option value="weekly">{t('teamManagement.weekly')}</option>
                            <option value="monthly">{t('teamManagement.monthly')}</option>
                            <option value="yearly">{t('teamManagement.yearly')}</option>
                        </select>
                    </div>
                </div>

                {/* Primary Team Member Selection */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '600',
                        color: 'var(--fg-primary)'
                    }}>
                        Primary Team Member *
                    </label>
                    <div style={{
                        marginBottom: '0.75rem',
                        fontSize: '0.85rem',
                        color: 'var(--muted)',
                        fontStyle: 'italic'
                    }}>
                        Select the first user to add to this team. You can add more members below with individual roles.
                    </div>
                    <select
                        value={defaultLiteLLMUserId}
                        onChange={(e) => setDefaultLiteLLMUserId(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            backgroundColor: 'var(--surface)',
                            color: 'var(--fg-primary)',
                            fontSize: '1rem'
                        }}
                        disabled={creating || litellmUsersLoading}
                    >
                        <option value="">Select a user...</option>
                        {litellmUsers
                            .filter(user => user.user_id !== 'default_user_id')
                            .map(user => (
                                <option key={user.user_id} value={user.user_id}>
                                    {user.user_email || user.user_id}
                                </option>
                            ))}
                    </select>

                    {/* Primary Member Role Selector */}
                    {defaultLiteLLMUserId && (
                        <div style={{ marginTop: '0.75rem' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '500',
                                color: 'var(--fg-primary)',
                                fontSize: '0.95rem'
                            }}>
                                Role for this member
                            </label>
                            <select
                                value={defaultUserRole}
                                onChange={(e) => setDefaultUserRole(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: '6px',
                                    backgroundColor: 'var(--surface)',
                                    color: 'var(--fg-primary)',
                                    fontSize: '1rem'
                                }}
                                disabled={creating}
                            >
                                <option value="admin">Admin (can manage team)</option>
                                <option value="user">User (can use team)</option>
                            </select>
                        </div>
                    )}
                </div>

                {/* Model Selection */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem'
                    }}>
                        <label style={{
                            fontWeight: '600',
                            color: 'var(--fg-primary)'
                        }}>
                            {t('teamCreate.models')} * ({selectedModels.length} {t('teamCreate.selected')})
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                onClick={selectAllModels}
                                disabled={creating}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'var(--surface)',
                                    color: 'var(--signal-blue)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: creating ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {t('teamCreate.selectAll')}
                            </button>
                            <button
                                onClick={clearAllModels}
                                disabled={creating}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'var(--surface)',
                                    color: 'var(--critical)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: creating ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {t('teamCreate.clearAll')}
                            </button>
                        </div>
                    </div>

                    <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        backgroundColor: 'var(--surface)',
                        padding: '0.75rem'
                    }}>
                        {uniqueModels.length === 0 ? (
                            <div style={{ color: 'var(--muted)', textAlign: 'center', padding: '1rem' }}>
                                {t('teamCreate.noModels')}
                            </div>
                        ) : (
                            uniqueModels.map((modelName) => (
                                <div
                                    key={modelName}
                                    style={{
                                        padding: '0.5rem',
                                        marginBottom: '0.25rem',
                                        borderRadius: '4px',
                                        backgroundColor: selectedModels.includes(modelName)
                                            ? 'var(--surface)'
                                            : 'transparent',
                                        cursor: creating ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onClick={() => !creating && toggleModel(modelName)}
                                >
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: creating ? 'not-allowed' : 'pointer',
                                        color: 'var(--fg-primary)'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedModels.includes(modelName)}
                                            onChange={() => toggleModel(modelName)}
                                            disabled={creating}
                                            style={{ marginRight: '0.5rem' }}
                                        />
                                        <span>{modelName}</span>
                                    </label>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Create API Key Option */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: creating ? 'not-allowed' : 'pointer',
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        backgroundColor: 'var(--surface)',
                        color: 'var(--fg-primary)'
                    }}>
                        <input
                            type="checkbox"
                            checked={createApiKey}
                            onChange={(e) => setCreateApiKey(e.target.checked)}
                            disabled={creating}
                            style={{ marginRight: '0.75rem' }}
                        />
                        <div>
                            <div style={{ fontWeight: '600' }}>Create API Key</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                                Automatically create an Ask Engine API key for this team
                            </div>
                        </div>
                    </label>
                </div>

                {/* User Selection (optional) */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem'
                    }}>
                        <label style={{
                            fontWeight: '600',
                            color: 'var(--fg-primary)'
                        }}>
                            Ask Front End Members (Optional) ({selectedUserEmails.length} {t('teamCreate.selected')})
                        </label>
                        {selectedUserEmails.length > 0 && (
                            <button
                                onClick={() => setSelectedUserEmails([])}
                                disabled={creating}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'var(--surface)',
                                    color: 'var(--critical)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: creating ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {t('teamCreate.clearAll')}
                            </button>
                        )}
                    </div>

                    {/* User search */}
                    {allUsers.length > 0 && (
                        <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder={t('teamCreate.searchUsers')}
                            style={{
                                width: '100%',
                                padding: '0.5rem 0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                backgroundColor: 'var(--surface)',
                                color: 'var(--fg-primary)',
                                fontSize: '0.875rem',
                                marginBottom: '0.5rem'
                            }}
                            disabled={creating}
                        />
                    )}

                    <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        backgroundColor: 'var(--surface)',
                        padding: '0.75rem'
                    }}>
                        {litellmUsersLoading ? (
                            <div style={{ color: 'var(--muted)', textAlign: 'center', padding: '1rem' }}>
                                Loading users...
                            </div>
                        ) : mergedUsers.length === 0 ? (
                            <div style={{ color: 'var(--muted)', textAlign: 'center', padding: '1rem' }}>
                                {t('teamCreate.noUsers')}
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div style={{ color: 'var(--muted)', textAlign: 'center', padding: '0.5rem' }}>
                                {t('teamCreate.noUsersMatch')}
                            </div>
                        ) : (
                            filteredUsers.map((user) => (
                                <div
                                    key={user.email}
                                    style={{
                                        padding: '0.5rem',
                                        marginBottom: '0.25rem',
                                        borderRadius: '4px',
                                        backgroundColor: selectedUserEmails.includes(user.email)
                                            ? 'var(--surface)'
                                            : 'transparent',
                                        cursor: creating ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onClick={(e) => {
                                        // Don't toggle if clicking on the select dropdown
                                        if (e.target.tagName === 'SELECT') return;
                                        if (!creating) toggleUser(user.email);
                                    }}
                                >
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: creating ? 'not-allowed' : 'pointer',
                                        color: 'var(--fg-primary)',
                                        gap: '0.5rem'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedUserEmails.includes(user.email)}
                                            onChange={() => toggleUser(user.email)}
                                            disabled={creating}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                {user.name || t('userDetails.noName')}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                                                {user.email}
                                            </div>
                                        </div>
                                        {/* Role selector - only show if user is selected */}
                                        {selectedUserEmails.includes(user.email) && (
                                            <select
                                                value={selectedUserRoles[user.email] || 'user'}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedUserRoles(prev => ({
                                                        ...prev,
                                                        [user.email]: e.target.value
                                                    }));
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                disabled={creating}
                                                style={{
                                                    padding: '0.25rem 0.5rem',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: '4px',
                                                    backgroundColor: 'var(--bg)',
                                                    color: 'var(--fg-primary)',
                                                    fontSize: '0.75rem',
                                                    cursor: creating ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                <option value="admin">Admin</option>
                                                <option value="user">User</option>
                                            </select>
                                        )}
                                    </label>
                                </div>
                            ))
                        )}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                        {t('teamCreate.membersOptional')}
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{
                        padding: '1rem',
                        backgroundColor: 'var(--critical-bg)',
                        color: 'var(--critical)',
                        borderRadius: '6px',
                        marginBottom: '1rem',
                        fontSize: '0.9rem'
                    }}>
                        {error}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    marginTop: '1.5rem'
                }}>
                    <Button
                        onClick={onClose}
                        variant="secondary"
                        disabled={creating}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleCreate}
                        variant="primary"
                        disabled={creating}
                    >
                        {creating ? t('teamCreate.creating') : t('teamCreate.createButton')}
                    </Button>
                </div>

                {/* Help Text */}
                <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface)',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: 'var(--muted)'
                }}>
                    <strong style={{ color: 'var(--fg-primary)' }}>{t('common.note')}:</strong> {t('teamCreate.helpText')}
                </div>
            </div>
        </Modal>
    );
};

export default TeamCreateModal;

